import { Component, ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "./ui/Button";

/**
 * Un errore di rendering non intercettato smonta l'intero albero React: senza
 * un confine l'utente vede una pagina bianca. Succede, per esempio, quando la
 * risposta del backend non ha un campo che la pagina legge (`undefined.length`).
 *
 * Due livelli, con lo stesso componente:
 *
 *  - `ConfineErroreApp`, in `main.tsx` attorno a tutto (router e provider
 *    compresi): l'ultima rete, per gli errori fuori dal layout (login, cambio
 *    password) o nel layout stesso. Non dipende da router né contesti, che
 *    potrebbero essere proprio la parte rotta, e offre solo «Ricarica».
 *  - `ConfinePagina`, dentro `AppLayout` attorno al contenuto: menu e header
 *    restano usabili, «Riprova» rimonta la pagina (che quindi rilegge i suoi
 *    dati) e il cambio di percorso lo azzera da solo.
 *
 * All'utente si mostra un messaggio generico: niente messaggio d'errore, stack
 * o dati. Il dettaglio va solo in console.
 */

interface ErrorBoundaryProps {
  /** Il livello del confine, per riconoscere da dove viene il log in console. */
  nome: string;
  /** Il riquadro al posto del contenuto; `riprova` rimonta il contenuto. */
  fallback: (riprova: () => void) => ReactNode;
  /** Quando cambia, un confine in errore torna al contenuto (es. il percorso). */
  chiaveReset?: unknown;
  children: ReactNode;
}

interface ErrorBoundaryState {
  inErrore: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { inErrore: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { inErrore: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[ErrorBoundary ${this.props.nome}] Errore di rendering in ${componenteDa(info.componentStack)}:`,
      error,
      info.componentStack,
    );
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.inErrore && !Object.is(prev.chiaveReset, this.props.chiaveReset)) this.setState({ inErrore: false });
  }

  riprova = () => this.setState({ inErrore: false });

  render() {
    return this.state.inErrore ? this.props.fallback(this.riprova) : this.props.children;
  }
}

/**
 * Il componente che ha lanciato: la prima voce dello stack dei componenti
 * («at PraticaDettaglio (…)»). Nella build di produzione i nomi sono
 * minificati, e lì conta lo stack che segue nel log.
 */
function componenteDa(stack: string | null | undefined): string {
  return stack?.match(/^\s*at (\S+)/m)?.[1] ?? "un componente sconosciuto";
}

/** Il confine globale: schermata intera e «Ricarica». */
export function ConfineErroreApp({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      nome="App"
      fallback={() => (
        <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface px-4 text-center">
          <AlertTriangle className="h-10 w-10 text-accent" aria-hidden="true" />
          <h1 className="text-xl font-bold text-ink">Qualcosa non ha funzionato</h1>
          <p className="max-w-sm text-sm text-ink/50">
            L&apos;applicazione ha avuto un problema imprevisto. Ricarica la pagina per riprovare.
          </p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Ricarica
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/** Il confine del contenuto di una pagina, dentro il layout: si azzera cambiando percorso. */
export function ConfinePagina({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary
      nome="Pagina"
      chiaveReset={pathname}
      fallback={(riprova) => (
        <div
          role="alert"
          className="mx-auto flex max-w-md flex-col items-center gap-3 rounded-card border border-line bg-white px-4 py-8 text-center shadow-card"
        >
          <AlertTriangle className="h-8 w-8 text-accent" aria-hidden="true" />
          <h1 className="text-lg font-bold text-ink">Questa pagina ha avuto un problema</h1>
          <p className="text-sm text-ink/50">Puoi riprovare, oppure passare a un&apos;altra pagina dal menu.</p>
          <Button variant="secondary" size="sm" onClick={riprova}>
            Riprova
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
